import PropTypes from 'prop-types';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { memo } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import IconButton from 'components/@extended/IconButton';

import { handlerDrawerOpen } from 'api/menu';
import HomeOutlined from '@ant-design/icons/HomeOutlined';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import HeartOutlined from '@ant-design/icons/HeartOutlined';
import SolutionOutlined from '@ant-design/icons/SolutionOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';
import AuditOutlined from '@ant-design/icons/AuditOutlined';
import DollarOutlined from '@ant-design/icons/DollarOutlined';
import InboxOutlined from '@ant-design/icons/InboxOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import BarChartOutlined from '@ant-design/icons/BarChartOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import FolderOutlined from '@ant-design/icons/FolderOutlined';
import LockOutlined from '@ant-design/icons/LockOutlined';
import { useSubscription } from 'contexts/SubscriptionContext';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

export const get3DIcon = (id) => {
  if (!id) return FolderOutlined;
  id = id.toLowerCase();
  
  if (id.includes('dashboard')) return HomeOutlined;
  if (id.includes('appointment')) return CalendarOutlined;
  if (id.includes('patient')) return HeartOutlined;
  if (id.includes('doctor')) return SolutionOutlined;
  if (id.includes('staff') || id.includes('employee')) return TeamOutlined;
  if (id.includes('hr') || id.includes('attendance') || id.includes('payroll')) return AuditOutlined;
  if (id.includes('billing') || id.includes('invoice') || id.includes('payment') || id.includes('expense')) return DollarOutlined;
  if (id.includes('inventory') || id.includes('stock') || id.includes('supplier') || id.includes('purchase')) return InboxOutlined;
  if (id.includes('communication') || id.includes('sms') || id.includes('whatsapp') || id.includes('notification')) return MessageOutlined;
  if (id.includes('report') || id.includes('revenue')) return BarChartOutlined;
  if (id.includes('setting') || id.includes('admin') || id.includes('role')) return SettingOutlined;
  
  return FolderOutlined;
};

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

const NavItem = memo(function NavItem({ item, level, isParents = false, setSelectedID }) {
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { isModuleUnlocked, openLockedModal } = useSubscription();

  const isLocked = !isModuleUnlocked(item);

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const itemHandler = (e) => {
    if (isLocked) {
      e?.preventDefault();
      openLockedModal({
        code: item.id,
        name: item.title,
        minimumTier: item.url?.includes('pharmacy') || item.url?.includes('report') ? 'enterprise' : 'professional'
      });
      return;
    }

    if (item.id === 'dental-notifications') {
      e?.preventDefault();
      window.dispatchEvent(new CustomEvent('toggle-notifications-drawer'));
      return;
    }

    if (downLG) handlerDrawerOpen(false);

    if (isParents && setSelectedID) {
      setSelectedID(item.id);
    }
  };

  const Icon = item.icon || get3DIcon(item.id);
  const itemIcon = <Icon className="nav-item-icon-svg" style={isParents ? { fontSize: 26 } : {}} />;

  const { pathname } = useLocation();
  const isSelected = !!matchPath({ path: item?.link ? item.link : item.url, end: false }, pathname);

  const textColor = 'text.secondary';
  const iconSelectedColor = 'primary.main';

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <ListItemButton
          {...(isLocked || item.id === 'dental-notifications' || item.url === '#' ? { component: 'div' } : { component: Link, to: item.url })}
          target={itemTarget}
          disabled={item.disabled}
          selected={isSelected}
          data-level={level}
          sx={(theme) => ({
            zIndex: 1201,
            pl: `${level * 28}px`,
            py: 1,
            boxShadow: 'none !important',
            border: 'none !important',
            outline: 'none !important',
            opacity: isLocked ? 0.75 : 1,
            transition: 'background-color 250ms ease-in-out',
            willChange: 'background-color'
          })}
          onClick={(e) => itemHandler(e)}
        >
          {itemIcon && (
            <ListItemIcon
              data-parents={isParents}
              sx={(theme) => ({
                minWidth: 28,
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isLocked ? theme.palette.text.disabled : theme.palette.primary.main,
                bgcolor: 'transparent',
                mr: 1.25,
                boxShadow: 'none',
                border: 'none',
                outline: 'none',
                transition: 'all 250ms ease-in-out'
              })}
            >
              {itemIcon}
            </ListItemIcon>
          )}
          <ListItemText
            sx={{
              margin: 0
            }}
            primary={
              <Typography 
                variant="body1" 
                sx={(theme) => ({ 
                  color: isSelected ? 'primary.main' : isLocked ? theme.palette.text.secondary : theme.palette.text.primary, 
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap' 
                })}
              >
                {item.title}
              </Typography>
            }
          />
          {isLocked && (
            <LockOutlined style={{ fontSize: 13, color: '#faad14', marginLeft: 6 }} />
          )}
          {item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
              sx={{
                overflow: 'hidden'
              }}
            />
          )}
        </ListItemButton>
        {item?.actions &&
          item?.actions.map((action, index) => {
            const ActionIcon = action.icon;
            const callAction = action?.function;
            return (
              <IconButton
                key={index}
                className="nav-item-action"
                {...(action.type === 'function' && {
                  onClick: (event) => {
                    event.stopPropagation();
                    callAction();
                  }
                })}
                {...(action.type === 'link' && {
                  component: Link,
                  to: action.url,
                  target: action.target ? '_blank' : '_self'
                })}
                color="secondary"
                variant="outlined"
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 20,
                  zIndex: 1202,
                  width: 20,
                  height: 20,
                  mr: -1,
                  ml: 1,
                  color: 'secondary.dark',
                  borderColor: isSelected ? 'primary.light' : 'secondary.light',
                  '&:hover': { borderColor: isSelected ? 'primary.main' : 'secondary.main' }
                }}
              >
                <ActionIcon style={{ fontSize: '0.625rem' }} />
              </IconButton>
            );
          })}
      </Box>
    </>
  );
});

export default NavItem;

NavItem.propTypes = {
  item: PropTypes.any,
  level: PropTypes.number,
  isParents: PropTypes.bool,
  setSelectedID: PropTypes.oneOfType([PropTypes.any, PropTypes.func])
};
